import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { api } from "@/lib/api"
import { ChevronUp, Paperclip, Send, X } from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "./Toast"
import { AutoSaveIndicator } from "./AutoSaveIndicator"
import { readDraftFromStorage, useAutoSaveDraft } from "@/hooks/useAutoSaveDraft"
import { notifyPendingSend, type PendingSendInfo } from "@/lib/undoSend"
import { platzhalterAufloesen } from "@/lib/signaturBilder"
import { searchHeroContacts, kontaktName, empfaengerMitKontakt, type HeroContactHit } from "@/lib/heroSearch"
import { wegSchritt } from "@/lib/wege"
import { datumHinweise } from "@/lib/sendePruefung"

/**
 * Antwort-Formular für eine einzelne E-Mail.
 * - Vorbelegt mit KI-Draft falls vorhanden ("Entwurf übernehmen")
 * - Attachments via <input type="file" multiple>
 * - Sendet multipart/form-data an POST /api/v1/emails/{id}/reply
 * - Nach Erfolg: Form klappt zu + Query-Invalidation
 */
export function ReplyForm({
  emailId,
  defaultTo,
  defaultSubject,
  aiDraft,
  tenantSlug,
  originalHtml,
  originalIsPlain,
  originalSender,
  originalDate,
  manualDraft,
  offen,
  onOffenChange,
  onEntwurfChange,
  planNeuSchreiben,
  onGesendet,
}: {
  emailId: number
  defaultTo: string
  defaultSubject: string
  aiDraft?: string | null
  tenantSlug?: string
  originalHtml?: string | null
  originalIsPlain?: boolean
  originalSender?: string
  originalDate?: string
  /** Vom Server zurueckgegebene Auto-Save-Werte — haben Vorrang vor den Defaults. */
  manualDraft?: {
    to?: string | null
    cc?: string | null
    subject?: string | null
    body?: string | null
    saved_at?: string | null
  } | null
  /** Von aussen gesteuert (Detailansicht): Entwurf auf- oder zugeklappt. */
  offen?: boolean
  onOffenChange?: (offen: boolean) => void
  /** Meldet jede Nutzer-Aenderung nach oben.
   *
   *  Hintergrund: der Haken "Antwort senden" im Agent-Plan verschickt
   *  `plan.reply.body`, dieses Formular speichert in `manual_draft_*`. Wer den
   *  Text hier anpasste und oben den Haken setzte, verschickte den alten
   *  Entwurf — die Aenderung wurde danach sogar geloescht. Was hier steht, ist
   *  jetzt das, was rausgeht. */
  onEntwurfChange?: (v: { to: string; cc: string; subject: string; body: string }) => void
  /** Plant neu und liefert den Antworttext des neuen Plans — derselbe Weg wie
   *  im Review (`/replan`, Planer-Modell). Wenn gesetzt, geht "Neu generieren"
   *  immer hier lang, auch mit eigener Anweisung (die wird zum Hinweis an den
   *  Planer). `/generate-draft` bleibt nur fuer Mails ohne Plan. */
  planNeuSchreiben?: (hinweis?: string) => Promise<{ body: string; subject?: string } | null>
  /** Meldet den verschickten Text, sobald die Antwort raus ist.
   *
   *  Damit fragt die Ansicht darueber nach, ob ein im Text genannter Termin
   *  als Blocker in den Kalender soll (#19) — vorher blieb das Blocken dem
   *  Gedaechtnis ueberlassen, und genau das ging unter. */
  onGesendet?: (text: string) => void
}) {
  // Wenn nur Plain-Text da ist, escapen + in <pre> fuer Darstellung + Versand.
  const effectiveHtml = originalHtml
    ? (originalIsPlain ? plainToHtml(originalHtml) : originalHtml)
    : ""

  // Tenant-Signatur fuer Live-Preview (Backend setzt sie beim Senden via {{SIGNATURE}}-Platzhalter ein).
  const { data: tenantData } = useQuery({
    queryKey: ["tenant", tenantSlug || ""],
    queryFn: async () => {
      if (!tenantSlug) return null
      const { data } = await api.GET("/api/v1/tenants/{slug}", { params: { path: { slug: tenantSlug } } })
      return data || null
    },
    enabled: !!tenantSlug,
    staleTime: 60_000,
  })
  const signatureHtml = tenantData?.signature_html || ""
  // Das Logo steht nicht in `signature_html`, sondern als Datei am Tenant; beim
  // Senden haengt es der Mailer als `cid:signature_logo` an die Signatur. In der
  // Vorschau fehlte es dadurch — man sah nicht, was tatsaechlich rausgeht.
  // Groessen wie im Versand (services/imap/_send.py).
  const signatureLogo = tenantData?.signature_logo_set
    ? `/api/v1/tenants/${encodeURIComponent(tenantSlug || "")}/signature-logo`
    : ""
  const qc = useQueryClient()
  const storageKey = `auto-draft:reply:${emailId}`
  // Vorrang: localStorage-Mirror (unflushed Edit) > server manual_draft_* > defaults.
  // Mirror wird ignoriert wenn er aelter ist als der Server-Save (z.B. Edit von einem
  // anderen Tab/Geraet) oder den Storage-Key nicht matcht.
  const stored = readDraftFromStorage<{ to: string; cc: string; subject: string; body: string }>(
    storageKey,
    { serverSavedAt: manualDraft?.saved_at },
  )
  const initialTo = stored?.to ?? manualDraft?.to ?? defaultTo
  const initialCc = stored?.cc ?? manualDraft?.cc ?? ""
  const fallbackSubject = defaultSubject?.toLowerCase().startsWith("re:")
    ? defaultSubject
    : `Re: ${defaultSubject || ""}`
  const initialSubject = stored?.subject ?? manualDraft?.subject ?? fallbackSubject
  const initialBody = stored?.body ?? manualDraft?.body ?? aiDraft ?? ""

  // Zugeklappt, bis der Nutzer ihn oeffnet. Der Composer stand frueher immer
  // offen und belegte mit An/CC/Betreff/Text/Signatur/Zitat den halben Schirm,
  // obwohl die meisten Mails gar nicht beantwortet werden.
  const [openIntern, setOpenIntern] = useState(false)
  const open = offen ?? openIntern
  const setOpen = (v: boolean) => { setOpenIntern(v); onOffenChange?.(v) }
  const [to, setTo] = useState(initialTo)
  const [cc, setCc] = useState(initialCc)
  const [subject, setSubject] = useState(initialSubject)
  const [includeQuote, setIncludeQuote] = useState(true)
  // #79 (Marlene, 07.09.2026): „die empfangene Email sollte im Verlauf auch
  // zu bearbeiten sein". null = unveraendertes Original; sonst der vom
  // Nutzer gekuerzte Klartext, der statt des Originals mitgeht.
  const [verlaufText, setVerlaufText] = useState<string | null>(null)
  const [verlaufBearbeiten, setVerlaufBearbeiten] = useState(false)
  const verlaufHtml = verlaufText == null ? effectiveHtml : plainToHtml(verlaufText)
  // Body wird mit KI-Entwurf vorbefuellt, damit der User ihn sofort sieht
  // (statt erst per Button-Klick). Auto-saved manueller Edit hat hoechste Prio.
  const [body, setBody] = useState(initialBody)
  const [files, setFiles] = useState<File[]>([])
  // #92: Datum-/Zeitfenster-Hinweise vor dem Senden. Nur ein Hinweis — der
  // zweite Klick („Trotzdem senden") schickt ab. Jede Änderung an Betreff
  // oder Text prüft beim nächsten Senden neu.
  const [datumWarnung, setDatumWarnung] = useState<{ fuer: string; hinweise: string[] } | null>(null)
  const [prueft, setPrueft] = useState(false)
  const pruefSchluessel = `${subject}\u0000${body}`
  const absenden = async () => {
    if (datumWarnung && datumWarnung.fuer === pruefSchluessel) {
      setDatumWarnung(null)
      m.mutate({})
      return
    }
    setPrueft(true)
    const hinweise = await datumHinweise(emailId, subject, body)
    setPrueft(false)
    if (hinweise.length > 0) {
      setDatumWarnung({ fuer: pruefSchluessel, hinweise })
      return
    }
    setDatumWarnung(null)
    m.mutate({})
  }
  const warnungAktuell = datumWarnung && datumWarnung.fuer === pruefSchluessel ? datumWarnung : null

  // Auto-Save: alle Reply-Felder, debounced, in manual_draft_*-Spalten.
  const autoSaveValues = useMemo(() => ({ to, cc, subject, body }), [to, cc, subject, body])
  const autoSave = useCallback(
    async (v: { to: string; cc: string; subject: string; body: string }) => {
      const r = await fetch(`/api/v1/emails/${emailId}/manual-draft`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(v),
      })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      // Gespeicherten Stand in den Mail-Cache schreiben (#131-Pruefung,
      // 27.09.2026). Sonst startet das Formular nach Mail-Wechsel und Zurueck
      // (Mail noch im Cache) mit dem ALTEN Serverstand oder dem KI-Text — und
      // das naechste Tippen ueberschreibt den echten Entwurf.
      qc.setQueryData(["email", String(emailId)], (alt: unknown) =>
        alt && typeof alt === "object"
          ? {
              ...(alt as Record<string, unknown>),
              manual_draft_to: v.to, manual_draft_cc: v.cc,
              manual_draft_subject: v.subject, manual_draft_body: v.body,
              manual_draft_saved_at: new Date().toISOString(),
            }
          : alt,
      )
    },
    [emailId, qc],
  )
  const { status: saveStatus, savedAt } = useAutoSaveDraft({
    values: autoSaveValues,
    save: autoSave,
    storageKey,
  })
  // Der Startwert ist keine Aenderung: sonst wuerde schon das blosse Oeffnen
  // den Plan-Text mit dem Formular-Vorbelegung ueberschreiben.
  const startwerte = useRef(autoSaveValues)
  // 🔴 Der Callback darf NICHT in den Dependencies stehen. Der Aufrufer gibt
  // ihn als Inline-Funktion mit — also bei jedem Render eine neue Referenz.
  // Und was er tut (`setQueryData` auf den Plan-Cache), loest ein Re-Render
  // aus. Beides zusammen ergibt einen geschlossenen Kreis:
  //   tippen → Effekt → onEntwurfChange → setQueryData → Re-Render →
  //   neue Callback-Referenz → Effekt → …
  // React bricht das nach ~50 Runden mit "Maximum update depth exceeded"
  // (#185) ab — die ganze Seite fliegt in die ErrorBoundary. Genau das
  // passierte am 10.08.2026 beim Antworten auf eine Mail.
  // Die Ref haelt den jeweils aktuellen Callback, ohne den Effekt zu takten.
  const entwurfCb = useRef(onEntwurfChange)
  entwurfCb.current = onEntwurfChange
  // Und gedrosselt, nicht bei jedem Zeichen: der Aufrufer schreibt damit in
  // den Plan-Cache, an dem die ganze Posteingangs-Seite haengt. Ungedrosselt
  // rechnet sie bei JEDEM Tastendruck neu — das Schreiben fuehlt sich zaeh an
  // (gemeldet 10.08.2026). 400 ms ist kurz genug, dass der Plan den Text hat,
  // wenn der Nutzer zum Ausfuehren greift, und lang genug fuers Tippen.
  const ausstehend = useRef<{ to: string; cc: string; subject: string; body: string } | null>(null)
  useEffect(() => {
    const v = autoSaveValues
    const s0 = startwerte.current
    if (v.to === s0.to && v.cc === s0.cc && v.subject === s0.subject && v.body === s0.body) return
    ausstehend.current = v
    const t = setTimeout(() => { ausstehend.current = null; entwurfCb.current?.(v) }, 400)
    return () => clearTimeout(t)
  }, [autoSaveValues])
  // Wird das Formular geschlossen, bevor die 400 ms um sind, geht der letzte
  // Stand sonst verloren — hier nachreichen.
  useEffect(() => () => {
    if (ausstehend.current) entwurfCb.current?.(ausstehend.current)
  }, [])
  const fileRef = useRef<HTMLInputElement | null>(null)
  /** Datei schwebt ueber dem Formular — Rahmen faerbt sich. */
  const [zieht, setZieht] = useState(false)
  /** CC ist selten; das Feld erscheint erst auf Wunsch. */
  const [ccOffen, setCcOffen] = useState(false)
  const [success, setSuccess] = useState(false)
  const [aiPrompt, setAiPrompt] = useState("")
  const [aiPromptOpen, setAiPromptOpen] = useState(false)

  const generate = useMutation({
    mutationFn: async (withPrompt: { prompt?: string; currentDraft?: string } = {}) => {
      // Mit Plan: neu planen wie im Review, damit Text und Termine aus
      // demselben Lauf stammen — die eigene Anweisung geht als Hinweis mit.
      if (planNeuSchreiben) {
        const d = await planNeuSchreiben(withPrompt.prompt || "")
        if (d?.body) return { ok: true, draft: d.body }
        if (!withPrompt.prompt) throw new Error("Der neue Plan sieht keine Antwort vor")
      }
      const res = await fetch(`/api/v1/emails/${emailId}/generate-draft`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          prompt: withPrompt.prompt || null,
          current_draft: withPrompt.currentDraft || null,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.detail || json.error || `HTTP ${res.status}`)
      return json as { ok: boolean; draft: string }
    },
    onSuccess: (d) => {
      setBody(d.draft)
      setAiPrompt("")
      setAiPromptOpen(false)
      toast("✓ KI-Entwurf generiert")
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
    },
    onError: (e) => {
      toast("KI-Entwurf fehlgeschlagen: " + (e instanceof Error ? e.message : String(e)), "error")
    },
  })

  const m = useMutation({
    mutationFn: async (opts: { force?: boolean } = {}) => {
      const buildBody = (forceFlag: boolean) => {
        const fd = new FormData()
        fd.append("to", to)
        fd.append("cc", cc)
        fd.append("subject", subject)
        fd.append("body", body)
        if (effectiveHtml) {
          // Bei deaktiviertem Toggle: HTML-Body trotzdem setzen (fuer Signatur +
          // Formatierung), aber ohne Original-Mail-Quote.
          const quoteHtml = includeQuote ? verlaufHtml : ""
          fd.append("body_html", buildReplyHtml(body, quoteHtml, originalSender, originalDate))
        }
        for (const f of files) fd.append("attachments", f)
        if (forceFlag) fd.append("force", "true")
        return fd
      }
      // HTML-Body mit User-Text + Quote-Block + Original-Mail-HTML.
      // Backend nutzt bei body_html gesetzt HTML-Multipart → Empfaenger sieht Bilder.
      const res = await fetch(`/api/v1/emails/${emailId}/reply`, {
        method: "POST",
        body: buildBody(!!opts.force),
        credentials: "include",
      })
      // Recipient-Mismatch: Backend blockt mit 422 + detail.code='recipient_mismatch'.
      // User-Confirm + Retry mit force=true. Schutz gegen Frontend-State-Bleed-Bugs
      // (Mail an falschen Empfaenger durch alte useState-Werte).
      if (res.status === 422) {
        try {
          const j = await res.json()
          const d = j.detail
          if (d && typeof d === "object" && d.code === "recipient_mismatch") {
            const expectedList = Array.isArray(d.expected) ? d.expected.join(", ") : "Original-Sender"
            const ok = window.confirm(
              `Achtung: Du antwortest an "${d.requested}", aber der urspruengliche Sender war ${expectedList}.\n\n` +
              `Wenn das ein Versehen ist, klick "Abbrechen" und korrigiere den Empfaenger.\n\n` +
              `Soll trotzdem an "${d.requested}" gesendet werden?`
            )
            if (!ok) {
              throw new Error("Abgebrochen — bitte Empfaenger pruefen")
            }
            // Retry mit force=true
            const res2 = await fetch(`/api/v1/emails/${emailId}/reply`, {
              method: "POST",
              body: buildBody(true),
              credentials: "include",
            })
            if (!res2.ok) {
              const t = await res2.text().catch(() => "")
              throw new Error(`HTTP ${res2.status}: ${t.slice(0, 200)}`)
            }
            return res2.json()
          }
        } catch (e) {
          if (e instanceof Error && e.message.startsWith("Abgebrochen")) throw e
          // sonst: durchfallen lassen zum Standard-Error-Handling unten
        }
      }
      if (!res.ok) {
        let msg = `HTTP ${res.status}`
        try {
          const j = await res.json()
          msg = j.detail || j.error || msg
        } catch { /* no-op */ }
        throw new Error(msg)
      }
      return res.json()
    },
    onSuccess: (data) => {
      wegSchritt("antwort_gesendet")
      // Gesendeten Stand merken: „Stoppen" im 30-s-Fenster holt ihn zurueck
      // (#131-Pruefung, 27.09.2026). Vorher war der Text nach dem Stoppen weg.
      const gesendet = { to, cc, subject, body }
      qc.invalidateQueries({ queryKey: ["email", String(emailId)] })
      qc.invalidateQueries({ queryKey: ["emails"] })
      setSuccess(true)
      // Vor `setBody("")` — danach ist der Text weg.
      try { onGesendet?.(body) } catch { /* die Nachfrage darf nichts kippen */ }
      setBody("")
      setFiles([])
      // localStorage-Mirror leeren — Backend hat manual_draft_* schon gecleant.
      try { localStorage.removeItem(storageKey) } catch { /* ignore */ }
      // Phase 6.4 Soft-Send: globalen Undo-Toast triggern wenn Backend
      // einen pending_send_id zurueckgegeben hat (30s Stoppen-Window).
      try {
        notifyPendingSend(data as PendingSendInfo, `Antwort an ${to}`, () => {
          setTo(gesendet.to); setCc(gesendet.cc)
          setSubject(gesendet.subject); setBody(gesendet.body)
          setSuccess(false); setOpen(true)
        })
      } catch { /* ignore */ }
      setTimeout(() => { setSuccess(false); setOpen(false) }, 2000)
    },
  })

  // Eine Zeile: worum es geht, ein Stueck Text, ein Weg hinein.
  const vorschau = (body || aiDraft || "").replace(/\s+/g, " ").trim()
  if (!open) {
    return (
      <div className="ev2-entwurf" onClick={() => setOpen(true)} role="button" tabIndex={0}
        onKeyDown={(ev) => { if (ev.key === "Enter" || ev.key === " ") { ev.preventDefault(); setOpen(true) } }}>
        <span className="ev2-entwurf-titel">{vorschau ? "Antwort-Entwurf" : "Antworten"}</span>
        {aiDraft && body === aiDraft && <span className="ev2-entwurf-ki">KI</span>}
        <span className="ev2-entwurf-vorschau">{vorschau || "Noch kein Text — hier schreiben."}</span>
        <span className="ev2-entwurf-link">Öffnen</span>
      </div>
    )
  }

  return (
    <form
      onSubmit={(ev) => { ev.preventDefault(); void absenden() }}
      className={cn("ev2-antwort", zieht && "zieht")}
      // Anhaenge per Drag & Drop: der Weg ueber den Datei-Dialog bleibt.
      onDragOver={(ev) => { ev.preventDefault(); if (!zieht) setZieht(true) }}
      onDragLeave={(ev) => {
        // Nur wenn das Formular wirklich verlassen wird, nicht bei jedem Kind.
        if (!ev.currentTarget.contains(ev.relatedTarget as Node)) setZieht(false)
      }}
      onDrop={(ev) => {
        ev.preventDefault()
        setZieht(false)
        const neue = Array.from(ev.dataTransfer.files || [])
        if (neue.length) setFiles((f) => [...f, ...neue])
      }}
    >
      <div className="ev2-antwort-kopf">
        <span className="ev2-antwort-titel">Antwort</span>
        {aiDraft && body === aiDraft && <span className="ev2-entwurf-ki">KI-Entwurf</span>}
        <AutoSaveIndicator status={saveStatus} savedAt={savedAt} />
        <span style={{ flex: 1 }} />
        <button type="button" onClick={() => setOpen(false)} className="ev2-pk-linkbtn"
          title="Zuklappen — der Entwurf bleibt gespeichert">
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="ev2-antwort-felder">
        <div className="ev2-antwort-zeile">
          <label htmlFor={`an-${emailId}`}>An</label>
          <RecipientField id={`an-${emailId}`} value={to} onChange={setTo} tenantSlug={tenantSlug || ""} />
          {!ccOffen && !cc && (
            <button type="button" className="ev2-pk-linkbtn" style={{ fontSize: 12 }}
              onClick={() => setCcOffen(true)}>+ CC</button>
          )}
        </div>
        {(ccOffen || cc) && (
          <div className="ev2-antwort-zeile">
            <label htmlFor={`cc-${emailId}`}>CC</label>
            <RecipientField id={`cc-${emailId}`} value={cc} onChange={setCc} placeholder="(optional)" tenantSlug={tenantSlug || ""} />
          </div>
        )}
        <div className="ev2-antwort-zeile">
          <label htmlFor={`betreff-${emailId}`}>Betreff</label>
          {/* Inline gesetzt wie im RecipientField — das globale Input-Styling
              des Portals ist spezifischer als die Klassenregel. */}
          <input id={`betreff-${emailId}`} value={subject} onChange={(e) => setSubject(e.target.value)}
            style={{
              flex: 1, minWidth: 0, border: "none", background: "none", padding: "6px 0",
              fontSize: 13.5, color: "var(--text)", outline: "none", height: "auto",
            }} />
        </div>
      </div>

      {/* Das Blatt: Text, Signatur, Verlauf — in der Reihenfolge, in der es
          beim Empfaenger ankommt. */}
      <div className="ev2-antwort-blatt">
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Antwort schreiben…"
          required
        />

        {(signatureHtml || signatureLogo) && (
          <div className="ev2-antwort-signatur" title="Signatur des Betriebs — wird automatisch angehängt">
            {signatureHtml && <div dangerouslySetInnerHTML={{ __html: platzhalterAufloesen(signatureHtml, {
              slug: tenantSlug || "",
              logoSet: tenantData?.signature_logo_set,
              bilder: tenantData?.signature_images,
            }) }} />}
            {signatureLogo && (
              <img src={signatureLogo} alt="Logo des Betriebs"
                style={{ maxWidth: 200, maxHeight: 80, marginTop: 8, display: "block" }} />
            )}
          </div>
        )}

        {effectiveHtml && (
          <div className="ev2-antwort-verlauf">
            <div className="ev2-antwort-verlauf-kopf">
              <span>
                {includeQuote
                  ? <>Am {originalDate ? new Date(originalDate).toLocaleString("de-DE") : "—"} schrieb {originalSender || "—"}:</>
                  : <em>Verlauf wird nicht mitgesendet.</em>}
              </span>
              <span style={{ display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
                {includeQuote && (
                  <button type="button" className="ev2-pk-linkbtn"
                    onClick={() => {
                      if (!verlaufBearbeiten && verlaufText == null) setVerlaufText(htmlZuText(effectiveHtml))
                      setVerlaufBearbeiten((v) => !v)
                    }}>
                    {verlaufBearbeiten ? "Fertig" : "Bearbeiten"}
                  </button>
                )}
                {includeQuote && verlaufText != null && (
                  <button type="button" className="ev2-pk-linkbtn"
                    onClick={() => { setVerlaufText(null); setVerlaufBearbeiten(false) }}>
                    Original
                  </button>
                )}
                <label style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer" }}>
                  <input type="checkbox" checked={includeQuote}
                    onChange={(e) => setIncludeQuote(e.target.checked)} />
                  mitsenden
                </label>
              </span>
            </div>
            {includeQuote && verlaufBearbeiten && (
              <textarea
                aria-label="Verlauf bearbeiten"
                value={verlaufText ?? ""}
                onChange={(e) => setVerlaufText(e.target.value)}
                style={{ width: "100%", minHeight: 180, fontSize: 13, lineHeight: 1.5, padding: 8,
                         border: "1px solid var(--border-color)", borderRadius: 6, fontFamily: "inherit" }}
              />
            )}
            {includeQuote && !verlaufBearbeiten && (
              <iframe
                title="Verlauf"
                srcDoc={`<!DOCTYPE html><html><head><meta charset="utf-8"><base target="_blank"><style>
                  html,body{margin:0;padding:0;max-width:100%;overflow-x:hidden}
                  body{padding:0;font-family:-apple-system,BlinkMacSystemFont,sans-serif;font-size:13px;color:#64748b;overflow-wrap:anywhere!important;word-break:break-word!important}
                  img{max-width:100%!important;height:auto!important}
                  a{word-break:break-all!important;overflow-wrap:anywhere!important;display:inline-block;max-width:100%}
                  pre,code{white-space:pre-wrap!important;word-break:break-all!important}
                  table{max-width:100%!important;width:auto!important;table-layout:auto}
                  td,th{max-width:100%!important;overflow-wrap:anywhere!important;word-break:break-word!important}
                  div,p,span,li{max-width:100%!important;overflow-wrap:anywhere!important;word-break:break-word!important}
                  *{box-sizing:border-box!important}
                </style></head><body>${verlaufHtml}</body></html>`}
                sandbox="allow-same-origin allow-popups"
                style={{ width: "100%", height: 180, border: "none", background: "transparent" }}
              />
            )}
          </div>
        )}
      </div>

      {files.length > 0 && (
        <div style={{ padding: "10px 18px 0", display: "flex", flexWrap: "wrap", gap: 6 }}>
          {files.map((f, i) => (
            <span key={i} className="ev2-antwort-anhang">
              <Paperclip className="w-3 h-3" /> {f.name}
              <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))}
                className="ev2-pk-linkbtn" title="Entfernen">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
        </div>
      )}

      {aiPromptOpen && (
        <div style={{ padding: "10px 18px 0" }}>
          <textarea
            value={aiPrompt}
            onChange={(e) => setAiPrompt(e.target.value)}
            rows={2}
            placeholder="Anweisung an die KI — z.B. kürzer, freundlicher, Termin Mi 14 Uhr vorschlagen"
            style={{
              width: "100%", fontSize: 13, padding: "8px 10px", borderRadius: 8,
              border: "1px solid var(--border-color)", background: "var(--bg-card)", resize: "vertical",
            }}
          />
          <div style={{ marginTop: 6, display: "flex", gap: 10, alignItems: "center" }}>
            <button type="button" disabled={generate.isPending || !aiPrompt.trim()}
              className="ev2-pk-primary" style={{ fontSize: 12.5, padding: "6px 12px" }}
              onClick={() => generate.mutate({ prompt: aiPrompt, currentDraft: body })}>
              {generate.isPending ? "Generiert…" : (body ? "Umformulieren" : "Generieren")}
            </button>
            <button type="button" className="ev2-pk-linkbtn" onClick={() => setAiPromptOpen(false)}>Abbrechen</button>
          </div>
        </div>
      )}

      {warnungAktuell && (
        <div role="alert" data-testid="datum-hinweise" style={{
          margin: "8px 0", fontSize: 12.5, color: "#92400e", background: "#fef3c7",
          border: "1px solid #fcd34d", borderRadius: 8, padding: "8px 10px",
        }}>
          <strong>Bitte kurz prüfen:</strong>
          <ul style={{ margin: "4px 0 0 16px", padding: 0 }}>
            {warnungAktuell.hinweise.map((h) => <li key={h}>{h}</li>)}
          </ul>
        </div>
      )}
      <div className="ev2-antwort-fuss">
        <button
          type="submit"
          disabled={m.isPending || prueft || !to || !body}
          className="ev2-pk-primary"
        >
          <Send className="w-3.5 h-3.5" /> {m.isPending ? "Sendet..." : prueft ? "Prüft…" : warnungAktuell ? "Trotzdem senden" : "Senden"}
        </button>

        <button type="button" className="ev2-pk-linkbtn" disabled={generate.isPending}
          onClick={() => generate.mutate({})}
          title="Schreibt den Text neu — mit den Terminen aus dem Plan">
          {generate.isPending ? "Generiert…" : (body ? "Neu generieren" : "KI-Entwurf generieren")}
        </button>
        <button type="button" className="ev2-pk-linkbtn" onClick={() => setAiPromptOpen(!aiPromptOpen)}>
          Mit Prompt anpassen
        </button>
        {aiDraft && body !== aiDraft && (
          <button type="button" className="ev2-pk-linkbtn" onClick={() => setBody(aiDraft)}>
            KI-Entwurf einfügen
          </button>
        )}

        <input ref={fileRef} type="file" multiple className="hidden"
          onChange={(e) => setFiles([...files, ...Array.from(e.target.files || [])])} />
        <button type="button" className="ev2-pk-linkbtn" onClick={() => fileRef.current?.click()}
          title="Datei wählen — oder einfach ins Formular ziehen">
          <Paperclip className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "-2px" }} /> Anhang
        </button>

        {/* #183: Zuklappen auch unten, wo man nach dem Schreiben steht — das X
            oben im Kopf hat niemand gefunden, und „Verwerfen“ loescht den Text. */}
        <button type="button" className="ev2-pk-linkbtn" onClick={() => setOpen(false)}
          title="Entwurf zuklappen — er bleibt gespeichert und steht beim nächsten Öffnen wieder da">
          <ChevronUp className="w-3.5 h-3.5" style={{ display: "inline", verticalAlign: "-2px" }} /> Zuklappen
        </button>

        <span style={{ flex: 1 }} />
        {m.isError && (
          <span style={{ fontSize: 12, color: "var(--danger-color)" }}>
            Fehler: {m.error instanceof Error ? m.error.message : "unbekannt"}
          </span>
        )}
        {success && <span style={{ fontSize: 12, color: "#059669", fontWeight: 600 }}>✓ Gesendet</span>}
        {zieht && <span style={{ fontSize: 12, color: "var(--accent-color)" }}>Datei hier loslassen…</span>}
      </div>
    </form>
  )
}

/** Wandelt Plain-Text in HTML um: escapen, Zeilenumbrueche als <br>, Links klickbar. */
/** HTML → Klartext fuers Bearbeiten des Verlaufs (#79). Blockgrenzen werden
 *  Zeilenumbrueche, Skripte und Styles fliegen raus. */
export function htmlZuText(html: string): string {
  if (!html) return ""
  const mitUmbruechen = html
    .replace(/<(script|style)[\s\S]*?<\/\1>/gi, "")
    .replace(/<(br|hr)\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|tr|h[1-6]|blockquote|pre|table)>/gi, "\n")
  let text: string
  try {
    const doc = new DOMParser().parseFromString(mitUmbruechen, "text/html")
    text = doc.body?.textContent || ""
  } catch {
    text = mitUmbruechen.replace(/<[^>]+>/g, " ")
  }
  return text.replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim()
}

function plainToHtml(txt: string): string {
  const esc = txt.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  const linked = esc.replace(/(https?:\/\/[^\s<]+)/g, (u) =>
    `<a href="${u}" style="color:#2563eb;word-break:break-all">${u}</a>`,
  )
  return `<div style="white-space:pre-wrap;font-family:-apple-system,sans-serif;font-size:13px;line-height:1.5">${linked}</div>`
}

/** Baut HTML-Body fuer Reply:
 *   User-Text + {{SIGNATURE}}-Platzhalter (Backend setzt Signatur ein) + Quote-Header + Original-HTML.
 *   So steht die Signatur ZWISCHEN User-Text und Original (nicht am Ende). */
function buildReplyHtml(userText: string, originalHtml: string, sender?: string, date?: string): string {
  const escUser = userText
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/\n/g, "<br>")
  // Quote-Block nur wenn originalHtml gesetzt ist (Toggle "Verlauf mitsenden").
  const dateLabel = date ? new Date(date).toLocaleString("de-DE") : ""
  const fromLabel = sender || ""
  const quoteBlock = originalHtml
    ? `<div style="border-top:1px solid #ddd;padding-top:10px;margin-top:10px;font-size:13px;color:#64748b">
         ${dateLabel ? `Am ${dateLabel}` : ""}${fromLabel ? ` schrieb ${fromLabel}` : ""}:
       </div>
       <blockquote style="margin:10px 0 0 0;padding:10px 15px;border-left:3px solid #cbd5e1;color:#334155">
         ${originalHtml}
       </blockquote>`
    : ""
  return `<div style="font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;font-size:14px;color:#0f172a">
    <div>${escUser}</div>
    {{SIGNATURE}}
    ${quoteBlock}
  </div>`
}

function RecipientField({
  id, value, onChange, placeholder, tenantSlug,
}: {
  /** Das Label steht in der Formularzeile daneben, nicht in dieser Komponente. */
  id?: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  tenantSlug: string
}) {
  const [hits, setHits] = useState<HeroContactHit[]>([])
  const [searching, setSearching] = useState(false)
  const [focused, setFocused] = useState(false)
  const debounceRef = useRef<number | null>(null)

  const handleChange = (v: string) => {
    onChange(v)
    if (debounceRef.current) window.clearTimeout(debounceRef.current)
    const q = v.trim()
    if (!tenantSlug || q.length < 2 || q.includes("@")) {
      setHits([])
      return
    }
    debounceRef.current = window.setTimeout(async () => {
      try {
        setSearching(true)
        const r = await searchHeroContacts(tenantSlug, q)
        // Ohne E-Mail-Adresse gibt es beim Klick nichts einzusetzen.
        setHits(r.filter((c) => !!c.email).slice(0, 8))
      } catch {
        setHits([])
      } finally {
        setSearching(false)
      }
    }, 250)
  }

  const pick = (c: HeroContactHit) => {
    // `kontaktName()` liefert notfalls „Kontakt #123" — das darf nur INNERHALB
    // der Adresse landen, nie als Empfaenger fuer sich.
    if (!c.email) { setHits([]); return }
    const addr = `"${kontaktName(c)}" <${c.email}>`
    // Mehrere Empfaenger: der getippte Suchbegriff wird ersetzt, nicht behalten.
    onChange(empfaengerMitKontakt(value, addr))
    setHits([])
  }

  return (
    <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
      <input
        id={id}
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setTimeout(() => setFocused(false), 150)}
        placeholder={placeholder}
        style={{
          width: "100%", border: "none", background: "none", padding: "6px 0",
          fontSize: 13.5, color: "var(--text)", outline: "none", height: "auto",
        }}
      />
      {focused && hits.length > 0 && (
        <div
          style={{
            position: "absolute", top: "100%", left: 0, right: 0, zIndex: 50,
            background: "var(--bg-card)", border: "1px solid var(--border-color)",
            borderRadius: 6, marginTop: 2, maxHeight: 240, overflowY: "auto",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          {hits.map((c) => {
            const name = kontaktName(c)
            return (
              <button
                key={c.id}
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(c) }}
                style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "6px 10px", border: "none", background: "none",
                  cursor: "pointer", fontSize: 12.5,
                }}
                className="hover:bg-secondary"
              >
                <div style={{ fontWeight: 600 }}>{name}</div>
                <div style={{ color: "var(--text-muted)", fontSize: 12 }}>
                  {[c.email, c.phone].filter(Boolean).join(" · ")}
                </div>
              </button>
            )
          })}
        </div>
      )}
      {searching && focused && (
        <div style={{ position: "absolute", right: 8, top: 28, fontSize: 12, color: "var(--text-muted)" }}>
          sucht…
        </div>
      )}
    </div>
  )
}
